import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { ThemeProvider } from '@mui/material/styles';
import XQTheme from './theme/xq-custom-theme';
import DataUploadWithData from './components/data upload/DataUpload';
import ProtectedRoute from './components/ProtectedRoute';
import Overview from './components/overview/Overview';
import Dashboard from './components/dashboard/Dashboard';
import Help from './components/help/Help';
import DataQcSummary from './components/data qc/DataQcSummary';
import TestScoresContentWithData from './components/data qc/test scores/TestScoresContent';
import CourseCatalogsContentWithData from './components/data qc/course catalog/CourseCatalogsContent';
import TranscriptsContentWithData from './components/data qc/transcripts/TranscriptsContent';
// import CteCoursesContentWithData from './components/data qc/cte courses/CteCoursesContent';
import DataQcLayout from './components/data qc/DataQcLayout';
import DataQcContentContainer from './components/data qc/DataQcContentContainer';
import RequiresTestScoresRoute from './components/data qc/RequiresTestScoresRoute';
import TranscriptsSideDrawerContentWithData from './components/data qc/transcripts/TranscriptsSideDrawerContent';
import TestScoresDrawerContentWithData from './components/data qc/test scores/TestScoresDrawerContent';
import CourseCatalogSideDrawerContentWithData from './components/data qc/course catalog/CourseCatalogSideDrawerContent';
// import CtePathwaysSideDrawerContentWithData from './components/data qc/cte courses/CtePathwaysSideDrawerContent';
import TranscriptSelection from './components/transcripts/TranscriptSelection';
import TranscriptSelectionsOverview from './components/transcripts/TranscriptSelectionsOverview';
import Login from './components/login/LoginPage';
import DistrictSelectionGate from './components/district-selection/DistrictSelectionGate';
import DistrictSelectionPage from './components/district-selection/DistrictSelectionPage';
import GlobalStateContextProvider from './context/GlobalStateContextProvider';
import NavigationLayout from './components/navigation/NavigationLayout';
import ErrorModelContextProvider from './context/ErrorModelContextProvider';
import RestrictedRoute from './components/RestrictedRoute';
import { USER_ROLES, NAVIGATION_LINKS_DATA, fileTypes, DATA_CATEGORY_LABELS, UPLOAD_SECTIONS_IDS } from './constants';
import SchoolFocusGroupsOverview from './components/focus-groups/SchoolFocusGroupsOverview';
import SchoolFocusGroup from './components/focus-groups/SchoolFocusGroups';
import FocusGroupReportForLeadFacilitator from './components/focus-groups/FocusGroupReportForLeadFacilitator';
import TranscriptDetails from './components/transcripts/details page/TranscriptDetails';
import TranscriptSelectionLayout from './components/transcripts/TranscriptSelectionLayout';
import ConfiguredSchools from './components/eoaConfigs/school config/ConfiguredSchools';
import EOAPage from './components/eoaConfigs/EOAPage';
import SchoolsConfiguration from './components/eoaConfigs/school config/SchoolsConfiguration';
import SchoolReportsOverview from './components/school-reports/SchoolReportsOverview';
import SurveyReportsOverview from './components/survey-reports/SurveyReportsOverview';
import StudentVoiceForReportsOverview from './components/student-voice/StudentVoiceForReportsOverview';
import StudentVoiceReview from './components/student-voice/StudentVoiceReview';
import DeveloperDashboard from './components/developer/DeveloperDashboard';
import SurveyManagement from './components/surveys/SurveyManagement';
import FocusGroupReportPocPage, {
  FOCUS_GROUP_REPORT_POC_PATH,
} from './components/focus-group-report-poc/FocusGroupReportPocPage';
import ChatPage from './components/chat/ChatPage';
import SurveyResponseDashboardPage from './components/survey-response-dashboard/SurveyResponseDashboardPage';
import PublicSurveyResponseDashboardPage from './components/survey-response-dashboard/PublicSurveyResponseDashboardPage';
import SjwPhotoUploadForSchool from './components/sjw-photo-upload/SjwPhotoUploadForSchool';
import SjwPhotoUploadOverview from './components/sjw-photo-upload/SjwPhotoUploadOverview';
import UserManagementLayout from './components/user-management/UserManagementLayout';
import UsersListPage from './components/user-management/UsersListPage';
import UserActivityLogPage from './components/user-management/UserActivityLogPage';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AcademicSettings from './components/eoaConfigs/academic settings/AcademicSettings';
import DSASettings from './components/eoaConfigs/dsa settings/DSASettings';
import DemographicSettings from './components/eoaConfigs/demographic settings/DemographicSettings';
import CreateEOA from './components/eoaConfigs/eoa/CreateEOA';
import CourseMappingPage from './components/course-mapping/CourseMappingPage';
import EoaConfigurationPage from './components/eoa-configuration/EoaConfigurationPage';
import ConfigurationLandingPage from './components/eoa-configuration/ConfigurationLandingPage';

const Auth0ProviderLayout = () => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK_URL || window.location.origin,
        audience: process.env.REACT_APP_BASE_URL,
      }}
    >
      <Outlet />
    </Auth0Provider>
  );
};

const routes = [
  { path: NAVIGATION_LINKS_DATA.OVERVIEW.path, element: <Overview /> },
  {
    path: NAVIGATION_LINKS_DATA.DASHBOARD.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR]}>
        <Dashboard />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.COURSE_MAPPING.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.DISTRICT_DATA_LEAD]}>
        <CourseMappingPage />
      </RestrictedRoute>
    ),
  },
  {
    path: '/data-upload',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.DISTRICT_DATA_LEAD, USER_ROLES.XQ_USER]}>
        <DataUploadWithData />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.DATA_QC.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.DISTRICT_DATA_LEAD, USER_ROLES.XQ_USER]}>
        <DataQcLayout />
      </RestrictedRoute>
    ),
    children: [
      { path: '', element: <DataQcSummary /> },
      {
        path: '/data-qc/test-scores',
        element: (
          <RequiresTestScoresRoute>
            <DataQcContentContainer
              drawerTitle="Test scores summary"
              cardTitle="Test scores"
              description="This section shows how the uploaded data, including student information and transcripts, are successfully matched and connected."
              cardContent={<TestScoresContentWithData />}
              drawerContent={<TestScoresDrawerContentWithData />}
              categoryName={DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES]}
              categoryId={UPLOAD_SECTIONS_IDS[fileTypes.TEST_SCORES]}
            />
          </RequiresTestScoresRoute>
        ),
      },
      {
        path: '/data-qc/course-catalogs',
        element: (
          <DataQcContentContainer
            drawerTitle="Course catalogs summary"
            cardTitle="Course catalogs"
            description=""
            cardContent={<CourseCatalogsContentWithData />}
            drawerContent={<CourseCatalogSideDrawerContentWithData />}
            sideDrawerDivider={false}
            categoryName={DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG]}
            categoryId={UPLOAD_SECTIONS_IDS[fileTypes.COURSE_CATALOG]}
          />
        ),
      },
      {
        path: '/data-qc/transcripts',
        element: (
          <DataQcContentContainer
            drawerTitle="Transcripts summary"
            cardTitle="Transcripts: EOAA cohort"
            description="View matching details for uploaded data, including student information and transcripts."
            cardContent={<TranscriptsContentWithData />}
            drawerContent={<TranscriptsSideDrawerContentWithData />}
            categoryName={DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS]}
            categoryId={UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_TRANSCRIPTS]}
          />
        ),
      },
      // {
      //   path: '/data-qc/cte-courses',
      //   element: (
      //     <DataQcContentContainer
      //       drawerTitle="CTE courses summary"
      //       cardTitle="CTE courses"
      //       description="Description placeholder"
      //       cardContent={<CteCoursesContentWithData />}
      //       drawerContent={<CtePathwaysSideDrawerContentWithData />}
      //       sideDrawerDivider={false}
      //       categoryName={DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS]}
      //       categoryId={UPLOAD_SECTIONS_IDS[fileTypes.CTE_PATHWAYS]}
      //     />
      //   )
      // },
    ],
  },
  {
    path: NAVIGATION_LINKS_DATA.FOCUS_GROUPS.path,
    element: (
      <RestrictedRoute
        allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.FACILITATOR, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.XQ_USER]}
      >
        <SchoolFocusGroupsOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.FOCUS_GROUP.path,
    element: (
      <RestrictedRoute
        allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.FACILITATOR, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.XQ_USER]}
      >
        <SchoolFocusGroup />
      </RestrictedRoute>
    ),
  },
  {
    path: '/focus-group-reports-lead-facilitator',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR]}>
        <FocusGroupReportForLeadFacilitator />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
    element: (
      <RestrictedRoute
        allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.FACILITATOR, USER_ROLES.XQ_USER, USER_ROLES.LEAD_FACILITATOR]}
      >
        <TranscriptSelectionsOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: '/transcripts',
    element: (
      <RestrictedRoute
        allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.FACILITATOR, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.XQ_USER]}
      >
        <TranscriptSelectionLayout />
      </RestrictedRoute>
    ),
    children: [
      { path: '', element: <TranscriptSelection /> },
      { path: '/transcripts/details', element: <TranscriptDetails /> },
    ],
  },
  {
    path: NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR]}>
        <SchoolReportsOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: '/survey-reports',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR]}>
        <SurveyReportsOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: '/student-voice-for-reports',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR]}>
        <StudentVoiceForReportsOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: '/student-voice-for-reports/:schoolId/review',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR]}>
        <StudentVoiceReview />
      </RestrictedRoute>
    ),
  },
  {
    path: '/developer-dashboard',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
        <DeveloperDashboard />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.SURVEY_MANAGEMENT.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
        <SurveyManagement />
      </RestrictedRoute>
    ),
  },
  {
    path: FOCUS_GROUP_REPORT_POC_PATH,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
        <FocusGroupReportPocPage />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.CONFIGURATION.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.XQ_ADMIN]}>
        <Outlet />
      </RestrictedRoute>
    ),
    children: [
      {
        path: '',
        element: <ConfigurationLandingPage />,
      },
      {
        path: ':eoaId/:stepId',
        element: <EoaConfigurationPage />,
      },
      {
        path: ':stepId',
        element: <EoaConfigurationPage />,
      },
    ],
  },
  {
    path: NAVIGATION_LINKS_DATA.SURVEY_RESPONSE_DASHBOARD.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR, USER_ROLES.XQ_ADMIN]}>
        <SurveyResponseDashboardPage />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR]}>
        <SjwPhotoUploadOverview />
      </RestrictedRoute>
    ),
  },
  {
    path: `${NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path}/:schoolId`,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN, USER_ROLES.LEAD_FACILITATOR, USER_ROLES.FACILITATOR]}>
        <SjwPhotoUploadForSchool />
      </RestrictedRoute>
    ),
  },
  { path: '/help', element: <Help /> },
  {
    path: '/eoa',
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
        <EOAPage />
      </RestrictedRoute>
    ),
    children: [
      {
        path: '/eoa/schools-configuration',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <SchoolsConfiguration />
          </RestrictedRoute>
        ),
      },
      {
        path: '/eoa/configured-schools',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <ConfiguredSchools />
          </RestrictedRoute>
        ),
      },
      {
        path: '/eoa/academic-settings',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <AcademicSettings />
          </RestrictedRoute>
        ),
      },
      {
        path: '/eoa/demographic-settings',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <DemographicSettings />
          </RestrictedRoute>
        ),
      },
      {
        path: '/eoa/dsa-settings',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <DSASettings />
          </RestrictedRoute>
        ),
      },
      {
        path: '/eoa/create-eoa',
        element: (
          <RestrictedRoute allowedRoles={[USER_ROLES.XQ_ADMIN]}>
            <CreateEOA />
          </RestrictedRoute>
        ),
      },
    ],
  },
  {
    path: NAVIGATION_LINKS_DATA.USER_MANAGEMENT.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.XQ_ADMIN]}>
        <UserManagementLayout />
      </RestrictedRoute>
    ),
    children: [{ path: '', element: <UsersListPage /> }],
  },
  {
    path: NAVIGATION_LINKS_DATA.ACTIVITY_LOG.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.XQ_ADMIN]}>
        <UserActivityLogPage />
      </RestrictedRoute>
    ),
  },
  {
    path: NAVIGATION_LINKS_DATA.CHAT.path,
    element: (
      <RestrictedRoute allowedRoles={[USER_ROLES.LEAD_FACILITATOR, USER_ROLES.SUPER_ADMIN]}>
        <ChatPage />
      </RestrictedRoute>
    ),
  },
];

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Auth0ProviderLayout />}>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<ErrorModelContextProvider />}>
          <Route element={<GlobalStateContextProvider />}>
            <Route element={<DistrictSelectionGate />}>
              <Route element={<NavigationLayout />}>
                <Route path={NAVIGATION_LINKS_DATA.SELECT_DISTRICT.path} element={<DistrictSelectionPage />} />
                {routes.map(({ path, element, children }, index) => (
                  <Route key={index} path={path} element={element}>
                    {children?.map(({ path, element }, index) => (
                      <Route key={index} path={path} element={element} />
                    ))}
                  </Route>
                ))}
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
      <Route
        path={`${NAVIGATION_LINKS_DATA.SURVEY_RESPONSE_DASHBOARD.path}/:eoaId`}
        element={<PublicSurveyResponseDashboardPage />}
      />
      <Route path="*" element={<Navigate to={'/login'} />} />
    </Route>
  )
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <ThemeProvider theme={XQTheme}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <RouterProvider router={router} />
    </LocalizationProvider>
  </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
